<template>
  <div class="promo-detail container py-4">
    <!-- 返回列：靠左 -->
    <div class="detail-header mb-2">
      <button class="back-btn detail-back-btn" type="button" @click="$router.back()">
        ← 返回當期活動
      </button>
    </div>

    <!-- 載入中 -->
    <div v-if="loading" class="text-center py-5">
      活動內容載入中…
    </div>

    <!-- 錯誤訊息 -->
    <div v-else-if="error" class="alert alert-danger">
      {{ error }}
    </div>

    <!-- 內容：markdown-it 渲染出來 -->
    <div v-else class="markdown-body" v-html="htmlContent"></div>
  </div>
</template>

<script>
import { supabase } from '@/lib/supabase'
import MarkdownIt from 'markdown-it'
import markdownItMark from 'markdown-it-mark'
import markdownItContainer from 'markdown-it-container'

export default {
  name: 'PromoDetail',
  data: () => ({
    loading: true,
    error: '',
    htmlContent: '',
    md: null
  }),

  methods: {
    // 初始化 markdown-it
    initMarkdown () {
      if (this.md) return

      const md = new MarkdownIt({
        html: true,
        linkify: true,
        breaks: true
      })

      md.use(markdownItMark)
      md.use(markdownItContainer, 'info', {
        render (tokens, idx) {
          const token = tokens[idx]
          if (token.nesting === 1) {
            return '<div class="md-info-box">\n'
          } else {
            return '</div>\n'
          }
        }
      })

      this.md = md
    },

    // 清掉 frontmatter（如果有）
    cleanFrontmatter (text) {
      return text.replace(/^---[\s\S]*?---\s*/m, '')
    },

    // 把 markdown 內相對路徑圖片 → Supabase public URL
    rewriteImagePaths (text, folderPath) {
      if (!folderPath) return text

      return text.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, alt, src) => {
        const trimmed = (src || '').trim()
        // 已經是 http(s) 就不要動
        if (/^https?:\/\//i.test(trimmed)) return match

        // 清掉開頭的 ./ 或 .\
        const cleanedPath = trimmed
          .replace(/^\.\/+/, '')
          .replace(/^\.\\+/, '')

        // 完整存放路徑：202601invite/202601invite.jpeg 這種
        const fullPath = `${folderPath}/${cleanedPath}`

        const { data } = supabase
          .storage
          .from('Event')
          .getPublicUrl(fullPath)

        if (!data || !data.publicUrl) {
          console.warn('[PromoDetail] image publicUrl not found for', fullPath)
          return match
        }

        return `![${alt}](${data.publicUrl})`
      })
    }
  },

  async mounted () {
    const folder = this.$route.params.folder
    if (!folder) {
      this.error = '找不到活動資料夾'
      this.loading = false
      return
    }

    this.initMarkdown()

    try {
      const folderPath = folder.replace(/\/+$/, '')

      // 1) 下載 index.md
      const { data: file, error: dlErr } = await supabase
        .storage
        .from('Event')
        .download(`${folderPath}/index.md`)

      if (dlErr) throw dlErr

      const rawMd = await file.text()

      // 這裡如果你之後要真的用 list() 做別的，也可以保留
      // 但光是圖片替換，其實靠 rewriteImagePaths 就夠用了
      // const { data: list, error: listErr } = await supabase
      //   .storage
      //   .from('Event')
      //   .list(folderPath)
      // if (listErr) throw listErr

      // 2) 清 frontmatter + 把相對圖片路徑改成 public URL
      const cleaned = this.cleanFrontmatter(rawMd)
      const withFixedImages = this.rewriteImagePaths(cleaned, folderPath)

      // 3) markdown-it 渲染
      this.htmlContent = this.md.render(withFixedImages)
    } catch (e) {
      console.error('[PromoDetail] error', e)
      this.error = '活動內容載入失敗，請稍後再試。'
    } finally {
      this.loading = false
    }
  }
}
</script>

<style scoped>
/* 活動詳情區：寬度限制 + 置中（跟 PaperDetail 類似） */
.promo-detail {
  max-width: 760px;
  margin: 0 auto;
}

/* 返回列：靠左、不要多餘間距 */
.detail-header {
  text-align: left;
}

.back-btn {
  margin-bottom: 0;
  background: none;
  border: 0;
  color: #0071bb;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
}

.detail-back-btn {
  font-size: 0.9rem;
}

.hint {
  padding: 16px;
  color: #555;
}
.hint.error {
  color: #b91c1c;
}

/* Markdown 外層排版 */
.markdown-body {
  line-height: 1.8;
  font-size: 0.95rem;
}

/* 整體文字靠左排 */
.markdown-body :deep(p),
.markdown-body :deep(ul),
.markdown-body :deep(ol),
.markdown-body :deep(li) {
  text-align: left;
}

/* 標題靠左 */
.markdown-body :deep(h1),
.markdown-body :deep(h2),
.markdown-body :deep(h3),
.markdown-body :deep(h4) {
  margin-top: 1.6rem;
  margin-bottom: 0.8rem;
  font-weight: 600;
  text-align: left;
}

.markdown-body :deep(h1) {
  font-size: 1.5rem;
}
.markdown-body :deep(h2) {
  font-size: 1.3rem;
}
.markdown-body :deep(h3) {
  font-size: 1.1rem;
}
.markdown-body :deep(h4) {
  font-size: 1rem;
}

.markdown-body :deep(p) {
  margin-bottom: 0.9rem;
}

.markdown-body :deep(ul),
.markdown-body :deep(ol) {
  padding-left: 1.4rem;
  margin-bottom: 0.9rem;
}

/* 圖片：寬度跟著文章寬度走 */
.markdown-body :deep(img) {
  display: block;
  width: 100%;
  max-width: 100%;
  height: auto;
  border-radius: 0.5rem;
  margin: 1.5rem auto;
}

/* 連結樣式 */
.markdown-body :deep(a) {
  color: #0d6efd;
  text-decoration: underline;
}

/* ==標記文字== */
.markdown-body :deep(mark) {
  background-color: #fff3cd;
  padding: 0 0.15em;
  border-radius: 0.25rem;
}

/* :::info 區塊樣式（對應 md-info-box） */
.markdown-body :deep(.md-info-box) {
  margin: 1.5rem 0;
  padding: 1rem 1.25rem;
  border-radius: 0.5rem;
  border-left: 4px solid #0d6efd;
  background: #f3f7ff;
  color: #333;
  font-size: 0.95rem;
}

.markdown-body :deep(.md-info-box p:last-child) {
  margin-bottom: 0;
}

/* 表格左對齊 */
.markdown-body :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 1.2rem 0;
}

.markdown-body :deep(th),
.markdown-body :deep(td) {
  text-align: left !important;
  padding: 8px 12px;
  vertical-align: top;
}

.markdown-body :deep(th) {
  font-weight: 600;
  border-bottom: 2px solid #e5e5e5;
}

.markdown-body :deep(td) {
  border-bottom: 1px solid #f0f0f0;
}
</style>
